// ============================================================================
// THE CAPTURE DRAFT — docs/design/handoff-mobile/README.md §1c and
// §State Management (`capture.draft { text, detectedType, typeOverride }`).
//
// TWO RULES, AND BOTH ARE BEHAVIOUR NOBODY WOULD GUESS FROM A SCREENSHOT:
//
// 1. IT DRAFTS. "Swipe down or scrim tap closes and discards nothing already
//    typed (it drafts)." The sheet is an overlay over a route that never
//    changes, so closing it is not cancelling — the half-typed thought is still
//    the thing you were doing, and re-opening the sheet has to find it there.
//    This is the opposite of the DESKTOP ⌘K palette, which resets on every open
//    (components/capture/CapturePalette.tsx): a keyboard palette is reopened by
//    reflex and a sheet is reopened on purpose.
//
// 2. THE OVERRIDE LATCHES. Detection runs on every keystroke; once a chip has
//    been pressed it stops moving underneath the finger. Without the latch,
//    typing one more word after choosing 'Note' would silently re-detect back to
//    'Task', which is the difference between a suggestion and a fight.
//
// It is a pure module so both rules are testable without a sheet, a pointer or a
// DOM — see mobile/mobile.test.ts.
// ============================================================================

import type { CaptureType } from '../../lib/data'
import type { Detection } from '../../components/capture/detect'

export interface CaptureDraft {
  /** What has been typed. Mirrors the field; the FIELD is the source of truth. */
  text: string
  /** The chip the owner pressed, or null. Only meaningful when `touched`. */
  typeOverride: CaptureType | null
  /** Has a chip been pressed at all? What makes the override latch. */
  touched: boolean
}

export const EMPTY_DRAFT: CaptureDraft = { text: '', typeOverride: null, touched: false }

export function withText(draft: CaptureDraft, text: string): CaptureDraft {
  return { ...draft, text }
}

/**
 * Press a chip. Pressing the ACTIVE one clears it back to unset — "optional —
 * unset goes to Inbox" is only true if unset is reachable, and on a phone the
 * only way back to it is the chip you just pressed.
 *
 * `touched` stays true after a clear: having deliberately unset the type is a
 * choice, and letting detection re-select one would undo it on the next letter.
 */
export function withType(
  draft: CaptureDraft,
  active: CaptureType | null,
  pressed: CaptureType,
): CaptureDraft {
  return { ...draft, touched: true, typeOverride: active === pressed ? null : pressed }
}

/** Which chip is lit: the owner's choice once there is one, the detection until then. */
export function draftType(draft: CaptureDraft, detection: Detection): CaptureType | null {
  return draft.touched ? draft.typeOverride : detection.type
}

/**
 * A save went through, so the draft is spent. NOT `EMPTY_DRAFT` by identity —
 * a fresh object, because the caller keeps this in state and a shared constant
 * that is never mutated is still a shared constant somebody will mutate.
 */
export function clearDraft(): CaptureDraft {
  return { text: '', typeOverride: null, touched: false }
}

/** Is there anything worth saving? An all-whitespace field is not. */
export function hasContent(draft: CaptureDraft): boolean {
  return draft.text.trim().length > 0
}
